import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, IconButton, List, ListItemButton, ListItemIcon, ListItemText, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import TodayIcon from '@mui/icons-material/Today';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import Groups2Icon from '@mui/icons-material/Groups2';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { getopcinesMenu } from '../services/apis';
import QuickreplyIcon from '@mui/icons-material/Quickreply';
import ConstructionIcon from '@mui/icons-material/Construction';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import SavingsIcon from '@mui/icons-material/Savings';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import { AconSemana } from '../global/acondia/AconSemana';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { TicketsUsuario } from '../global/TicketsUsuario';
import { SideTodayEvents } from '../global/SideTodayEvents';
import { SideUserAccounting } from '../global/SideUserAccounting';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import CameraEnhanceIcon from '@mui/icons-material/CameraEnhance';
import SettingsIcon from '@mui/icons-material/Settings';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import CloseIcon from '@mui/icons-material/Close';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

const iconMapping = {
    TodayIcon: <TodayIcon />,
    FolderSpecialIcon: <FolderSpecialIcon />,
    Groups2Icon: <Groups2Icon />,
    AdminPanelSettingsIcon: <AdminPanelSettingsIcon />,
    QuickreplyIcon: <QuickreplyIcon />,
    AconSemana: <AconSemana />,
    ConstructionIcon: <ConstructionIcon />,
    AccessAlarmIcon: <AccessAlarmIcon />,
    SavingsIcon: <SavingsIcon />,
    LocalDiningIcon: <LocalDiningIcon />,
    AssignmentIcon: <AssignmentIcon />,
    CheckroomIcon: <CheckroomIcon />,
    CameraEnhanceIcon: <CameraEnhanceIcon />,
    SettingsIcon: <SettingsIcon />,
    LocalActivityIcon: <LocalActivityIcon />,
    AccountCircleOutlinedIcon: <AccountCircleOutlinedIcon />
};

export const MenuLateral = ({ onNavigate, onClose, nombreCliente = 'Penya App', mobileDrawer = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const currentYear = new Date().getFullYear();
    const [loaded, setLoaded] = useState(false);
    const [opciones, setOpciones] = useState([]);
    const [anioSeleccionado, setAnioSeleccionado] = useState(currentYear);
    const [aniosDisponibles, setAniosDisponibles] = useState([currentYear]);

    const cargarOpciones = useCallback(async () => {
        try {
            const response = await getopcinesMenu();
            setOpciones(response.data);
            setLoaded(true);
        } catch (error) {
            console.error('Error en carregar les opcions del men?:', error);
            localStorage.clear();
            navigate("/login");
        }
    }, [navigate]);

    useEffect(() => {
        cargarOpciones();
    }, [cargarOpciones]);

    const handleYearsChange = useCallback((years = []) => {
        const normalizedYears = years
            .map((year) => Number(year))
            .filter((year) => Number.isInteger(year) && year > 0);
        const nextYears = Array.from(new Set([currentYear, anioSeleccionado, ...normalizedYears]))
            .sort((a, b) => b - a);

        setAniosDisponibles(nextYears);
    }, [anioSeleccionado, currentYear]);

    const handleNavigate = (url) => {
        navigate(url);
        onNavigate?.();
    };

    const isActiveRoute = (url) => {
        if (!url) {
            return false;
        }

        return location.pathname === url || (url !== "/" && location.pathname.startsWith(url));
    };

    const navOptions = opciones.filter((opcion) => Number(opcion.tipo) === 0);
    return (
        <Box
            className="side-menu fade-in-2"
            sx={mobileDrawer ? { width: 'min(92vw, 390px)' } : { width: '100%' }}
        >
            <Box className="side-menu-brand">
                <Box className="side-menu-brand-copy">
                    <Typography className="side-menu-eyebrow">Penya</Typography>
                    <Typography variant="h6" className="side-menu-title">
                        {nombreCliente || 'Penya App'}
                    </Typography>
                    <TextField
                        fullWidth
                        select
                        label="Any"
                        size="small"
                        value={anioSeleccionado}
                        onChange={(event) => setAnioSeleccionado(Number(event.target.value))}
                        className="side-year-select"
                        sx={{ width: {xs: '120%', sm: '115%'}, ml: -1}}
                    >
                        {aniosDisponibles.map((year) => (
                            <MenuItem key={year} value={year}>
                                {year}
                            </MenuItem>
                        ))}
                    </TextField>
                </Box>
                {mobileDrawer && (
                    <IconButton
                        className="side-menu-close"
                        onClick={onClose}
                        aria-label="Tancar men?"
                    >
                        <CloseIcon />
                    </IconButton>
                )}
            </Box>

            {!loaded && (
                <Box className="side-menu-loading">
                    <CircularProgress size={28} />
                </Box>
            )}

            {loaded && (
                <>
                    <Stack className="side-menu-widgets">
                        <SideTodayEvents />
                        <TicketsUsuario year={anioSeleccionado} />
                        <SideUserAccounting year={anioSeleccionado} onYearsChange={handleYearsChange} />
                    </Stack>

                    <Typography className="side-section-label">
                        Accessos
                    </Typography>
                    <List className="side-menu-list" disablePadding>
                        <ListItemButton
                            className={`side-menu-item ${isActiveRoute('/despesas/nova') ? 'side-menu-item-active' : ''}`}
                            onClick={() => handleNavigate('/despesas/nova')}
                        >
                            <ListItemIcon className="side-menu-icon"><ReceiptLongIcon /></ListItemIcon>
                            <ListItemText primary="Afegir despesa" primaryTypographyProps={{ className: 'side-menu-label' }} />
                        </ListItemButton>
                        {navOptions.map((opcion) => {
                            const active = isActiveRoute(opcion.url);

                            return (
                                <ListItemButton
                                    key={opcion.id}
                                    className={`side-menu-item ${active ? 'side-menu-item-active' : ''}`}
                                    onClick={() => handleNavigate(opcion.url)}
                                >
                                    <ListItemIcon className="side-menu-icon">
                                        {iconMapping[opcion.icon] || <AssignmentIcon />}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={opcion.nombre}
                                        primaryTypographyProps={{
                                            className: 'side-menu-label',
                                        }}
                                    />
                                </ListItemButton>
                            );
                        })}
                    </List>
                </>
            )}
        </Box>
    );
};
